"use client";

import "./App.css";
import { useState, useEffect } from "react";
import Vapi from "@vapi-ai/web";
import { Phone, PhoneOff, Mic, Volume2, MicOff, ChevronDown, ChevronUp } from "lucide-react";

const vapi = new Vapi("9218eb39-bc58-4da2-bcfb-6b4fc4a17bab");

function App() {
  
  const [content, setContent] =
    useState(`You are a friendly and helpful Starbucks virtual assistant with a warm, welcoming female voice. Your goal is to take coffee orders from customers just like a real Starbucks barista.

Start by greeting the customer with enthusiasm and introduce yourself. Ask for their name naturally to create a personalized experience. Guide them through the Starbucks menu, suggesting popular drinks and customization options (like size, milk type, and additional flavors).

After confirming their order, politely ask for their delivery address and preferred payment method. Make small talk when appropriate, such as complimenting their choice or asking if they'd like to add a snack.

Always maintain a cheerful, human-like tone, ensuring a smooth and friendly ordering experience. Once the order is complete, summarize it and give an estimated delivery time before ending the conversation on a positive note.`);
  const [callActive, setCallActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [callLogs, setCallLogs] = useState([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showAssistantContext, setShowAssistantContext] = useState(true);

  useEffect(() => {
    vapi.on("speech-start", () => {
      setIsSpeaking(true);
      addLog("Assistant started speaking");
    });

    vapi.on("speech-end", () => {
      setIsSpeaking(false);
      addLog("Assistant finished speaking");
    });

    vapi.on("call-start", () => {
      setIsConnecting(false);
      setCallActive(true);
      addLog("Call started");
      vapi.send({
        type: "add-message",
        message: {
          role: "system",
          content: "Hello! Welcome to FlowVoice. How can I assist you today?",
        },
      });
    });

    vapi.on("call-end", () => {
      setCallActive(false);
      setIsSpeaking(false);
      setVolumeLevel(0);
      setIsConnecting(false);
      setIsMuted(false);
      addLog("Call ended");
    });

    vapi.on("volume-level", (volume) => {
      setVolumeLevel(volume * 100);
    });

    return () => {
      vapi.removeAllListeners();
    };
  }, []);

  const addLog = (message) => {
    setCallLogs((prev) => [
      ...prev,
      {
        timestamp: new Date().toLocaleTimeString(),
        message,
      },
    ]);
  };

  const startCall = () => {
    setCallLogs([]);
    setIsConnecting(true);
    vapi.start({
      transcriber: {
        provider: "deepgram",
        model: "nova-3",
        language: "en-US",      
      },
      model: {
        provider: "openai",
        model: "gpt-4o-mini",    
        messages: [
          {
            role: "system",
            content: content,
          },
        ],
      },
      voice: {
        provider: "openai",
        voiceId: "nova",      
      },
      name: "Westack AI Assistant",
      firstMessageMode:"assistant-speaks-first"
    });
  };

  const endCall = () => {
    vapi?.stop();
  };

  const toggleMute = () => {
    if (callActive) {
      if (isMuted) {
        vapi.setMuted(false);
        addLog("Microphone unmuted");
      } else {
        vapi.setMuted(true);
        addLog("Microphone muted");
      }
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-5 min-h-[600px]">
          {/* Main panel - takes 3/5 on desktop */}
          <div className="md:col-span-3 p-6 border-r border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-indigo-900 flex items-center">
                <Mic className="w-6 h-6 mr-2 text-indigo-600" />
                FlowVoice Assistant
              </h1>
            </div>
            
            {/* Assistant Context Section with collapsible behavior */}
            <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
              <button 
                onClick={() => setShowAssistantContext(!showAssistantContext)}
                className="w-full p-4 bg-gray-50 flex items-center justify-between text-left border-b border-gray-200 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <span className="font-medium text-gray-800 mr-2">Assistant Context</span>
                  <span className="ml-2 text-sm text-gray-500">{showAssistantContext ? "(Click to collapse)" : "(Click to expand)"}</span>
                </div>
                {showAssistantContext ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {showAssistantContext && (
                <div className="p-4 animate-slideDown">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Enter the context and instructions for your AI assistant..."
                    disabled={callActive}
                    rows={8}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 resize-none transition-all disabled:bg-gray-50 disabled:text-gray-500"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    This text defines how your assistant will behave during the conversation.
                  </p>
                </div>
              )}
            </div>

            {/* Voice Visualization */}
            <div className="flex flex-col items-center justify-center pt-6">
              <div className="relative w-64 h-64 flex items-center justify-center">
                <div className={`absolute w-64 h-64 rounded-full bg-indigo-100 opacity-30 ${isSpeaking ? 'animate-ping' : ''}`}></div>
                <div className="absolute w-48 h-48 rounded-full bg-indigo-200 opacity-50"></div>
                <div className="relative w-32 h-32 rounded-full bg-indigo-600 flex items-center justify-center shadow-lg">
                  {isSpeaking ? (
                    <Volume2 className="w-12 h-12 text-white animate-pulse" />
                  ) : (
                    <Mic className="w-12 h-12 text-white" />
                  )}
                </div>
              </div>
              
              <div className="mt-10 w-full max-w-md">
                <div className="flex items-center mb-2">
                  <div className="text-sm font-medium text-gray-700 mr-2">Volume Level</div>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-600 transition-all duration-100"
                      style={{ width: `${volumeLevel}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="flex justify-between mt-6 gap-4">
                  <button
                    onClick={startCall}
                    disabled={callActive}
                    className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 ${
                      callActive
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800'
                    } transition-colors shadow-md`}
                  >
                    <Phone className="w-5 h-5" />
                    <span>{isConnecting ? "Connecting..." : "Start Call"}</span>
                  </button>
                  
                  <button
                    onClick={endCall}
                    disabled={!callActive}
                    className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 ${
                      !callActive
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800'
                    } transition-colors shadow-md`}
                  >
                    <PhoneOff className="w-5 h-5" />
                    <span>End Call</span>
                  </button>
                  
                  <button
                    onClick={toggleMute}
                    disabled={!callActive}
                    className={`py-3 px-4 rounded-lg flex items-center justify-center ${
                      !callActive
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : isMuted
                        ? 'bg-amber-600 text-white hover:bg-amber-700'
                        : 'bg-gray-700 text-white hover:bg-gray-800'
                    } transition-colors shadow-md`}
                  >
                    {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                </div>
                
                <div className="flex justify-center mt-4">
                  <div className={`flex items-center ${callActive ? 'text-green-600' : 'text-gray-500'} text-sm font-medium`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${callActive ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                    {isConnecting ? "Connecting..." : callActive ? "Call Active" : "Ready to Start"}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Activity log - takes 2/5 on desktop */}
          <div className="md:col-span-2 bg-gray-50 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Activity Log</h2>
              {callLogs.length > 0 && (
                <button 
                  onClick={() => setCallLogs([])}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
            
            <div className="h-[500px] overflow-y-auto pr-2 custom-scrollbar">
              {callLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Volume2 className="w-12 h-12 mb-4 opacity-20" />
                  <p>No activity yet</p>
                  <p className="text-sm mt-2">Start a call to see activities</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {callLogs.map((log, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                      <div className="flex justify-between items-start">
                        <p className="text-gray-800">{log.message}</p>
                        <span className="text-xs text-gray-400 whitespace-nowrap ml-4">{log.timestamp}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add custom styling for the activity log scrollbar and animations */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-in-out;
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-ping {
          animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        @keyframes ping {
          0% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.05); opacity: 0.4; }
          100% { transform: scale(1); opacity: 0.3; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default App;